import {
    UserInfo,
    ConversationRequest,
    Conversation,
    ChatMessage,
    CosmosDBHealth,
    CosmosDBStatus,
    AuthorizationPayload,
    History,
    ListOfFiles,
    UserData,
    UserMfaSetupInfo,
    UserLocalInfo,
    contractStatus,
    Config,
    FeatureFlags,
    UIConfig,
} from "./models";
import { chatHistorySampleData } from "../constants/chatHistory";
import appStore from "../store/appStore";
import { toast } from "react-toastify";
import defaultFrontendSettings from "../../frontend_settings.json";
import defaultFeatureSettings from "../../feature_flags.json";

const headers = {
    Authorization: `Bearer`,
};
const host = window.location.hostname === 'localhost' ? "http://localhost:8000/api" : "/api";

// refresh token related logic
export async function refreshAccessToken(): Promise<any> {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const response = await fetch(host + "/auth/refresh-token", {
        method: "POST",
        headers: {
            Authorization: `Bearer ${user.access_token}`,
        },
    });
    const data = await response.json();
    if (data.message === "ExpiredRefreshError") {
        window.location.href = "/";
        localStorage.removeItem("user");
        return;
    }
    const updatedUser = {
        ...user,
        access_token: data.access_token,
    };
    localStorage.setItem("user", JSON.stringify(updatedUser));

    return updatedUser;
}

export async function fetchWithAuth(
    url: string,
    options: RequestInit,
): Promise<Response> {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    let token = user.access_token;

    // Set the Authorization header
    const headers = {
        ...options.headers,
        Authorization: `Bearer ${token}`,
    };

    // Make the initial fetch request
    let response = await fetch(url, { ...options, headers });

    // Check if the response indicates an expired token
    if (response.status === 401) {
        const errorResponse = await response.json();
        if (errorResponse.payload === "Token Expired" || errorResponse.msg === 'ExpiredAccessError') {
            // Attempt to refresh the token
            try {
                await refreshAccessToken(); // Refresh the token
                const updatedUser = JSON.parse(
                    localStorage.getItem("user") || "{}",
                );
                token = updatedUser.access_token; // Get the new access token

                // Retry the original request with the new token
                const retryHeaders = {
                    ...options.headers,
                    Authorization: `Bearer ${token}`,
                };
                response = await fetch(url, {
                    ...options,
                    headers: retryHeaders,
                });
            } catch (refreshError) {
                const err: any = refreshError;
                toast.error("Failed to refresh token:", err);
                throw refreshError; // Propagate the error if token refresh fails
            }
        } else {
            window.location.href = "/";
            localStorage.removeItem("user");
        }
    }

    // Return the final response
    return response;
}

export async function conversationApi(
    options: ConversationRequest,
    abortSignal: AbortSignal,
): Promise<Response> {
    const response = await fetchWithAuth(host + "/conversation", {
        method: "POST",
        body: JSON.stringify({
            messages: options.messages,
        }),
        signal: abortSignal,
    });

    return response;
}

export async function authorization(
    payload: AuthorizationPayload,
): Promise<any> {
    try {
        const response = await fetch(host + "/auth/getAToken", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
        });
        if (!response.ok) {
            return null;
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const userData = {
            username: "",
            role: "",
            email: "",
            access_token: data.access_token,
        };
        localStorage.setItem("user", JSON.stringify(userData));

        return userData;
    } catch (error) {
        const err: any = error;
        toast.error("Error during API request:", err);
        throw error;
    }
}

export const getUserInfo = async (): Promise<any> => {
    const response = await fetchWithAuth(
        host + `/auth/identity`,
        {
            method: "GET",
        },
    );
    const payload = await response.json();
    return payload;
};

export const getCitationData = async (fileId: string, pageNumber: string) => {
    const response = await fetchWithAuth(
        host + `/chat/citation?file_id=${fileId}&page_label=${pageNumber}`,
        {
            method: "GET",
        },
    )
        .then(async (res) => {
            const payload = await res.json();
            return payload;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return null;
        });
    return response;
};

export const historyList = async (offset = 0): Promise<History[] | null> => {
    const response = await fetchWithAuth(
        host + `/chat/history/list?offset=${offset}`,
        {
            method: "GET",
        },
    )
        .then(async (res) => {
            const payload = await res.json();
            return payload;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return null;
        });
    return response;
};

export async function updateConfig(indexId: string, config: any): Promise<any> {
    try {
        const response = await fetchWithAuth(
            host + `/client-mgmt/config/${indexId}`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(config),
            }
        );
        if (!response.ok) throw new Error('Failed to update config');
        return await response.json();
    } catch (err) {
        console.error("There was an issue updating the UI configuration.", err);
        throw err;
    }
}

export async function getDefaultConfig(): Promise<Config> {
    const defaultFeatureFlags: FeatureFlags = {
    ...defaultFeatureSettings,
    fileShared: defaultFeatureSettings.features.fileShared,
    question: Array.isArray(defaultFeatureSettings.question)
      ? defaultFeatureSettings.question
      : Object.entries(defaultFeatureSettings.question || {}).map(([k, v]) => ({ int: v })),
};
    const defaultUIConfig: UIConfig = defaultFrontendSettings;
    try {
        const response = await fetchWithAuth(
            host + `/client-mgmt/config`,
            {
                method: "GET",
            }
        );
        const payload = await response.json();
        const featureFlags: FeatureFlags = payload.data.feature_flags;
        const uiConfig: UIConfig = payload.data.ui_config;
        uiConfig.title = payload.data.title;
        const config: Config = {
            featureFlags: featureFlags,
            uiConfig: uiConfig,
        };
        console.log(config);
        return config;
    } catch (err) {
        console.log(err);
        console.error("There was an issue fetching the UI configuration.");
        // Return default configuration that enables all features
        const config: Config = {
            featureFlags: defaultFeatureFlags,
            uiConfig: defaultUIConfig,
        };
        console.log(config);
        return config;
    }
}

export async function getConfig(indexId: string): Promise<Config> {
    const defaultFeatureFlags: FeatureFlags = {
    ...defaultFeatureSettings,
    fileShared: defaultFeatureSettings.features.fileShared,
    question: Array.isArray(defaultFeatureSettings.question)
      ? defaultFeatureSettings.question
      : Object.entries(defaultFeatureSettings.question || {}).map(([k, v]) => ({ int: v })),
};
    const defaultUIConfig: UIConfig = defaultFrontendSettings;
    try {
        const response = await fetchWithAuth(
            host + `/client-mgmt/config/${indexId}`,
            {
                method: "GET",
            }
        );
        const payload = await response.json();
        const featureFlags: FeatureFlags = payload.data.feature_flags;
        const uiConfig: UIConfig = payload.data.ui_config;
        uiConfig.title = payload.data.title;
        const config: Config = {
            featureFlags: featureFlags,
            uiConfig: uiConfig,
        };
        console.log(config);
        return config;
    } catch (err) {
        console.log(err);
        console.error("There was an issue fetching the UI configuration.");
        // Return default configuration that enables all features
        const config: Config = {
            featureFlags: defaultFeatureFlags,
            uiConfig: defaultUIConfig,
        };
        console.log(config);
        return config;
    }
}

export const historyRead = async (convId: string): Promise<any> => {
    const response = await fetchWithAuth(host + "/chat/history/read", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
        }),
    })
        .then(async (res) => {
            if (!res) {
                return [];
            }
            const payload = await res.json();
            let messages: ChatMessage[] = [];
            if (payload?.messages) {
                payload.messages.forEach((msg: any) => {
                    const message: ChatMessage = {
                        id: msg.id,
                        role: msg.role,
                        date: msg.createdAt,
                        content: msg.content,
                        feedback: msg.feedback ?? undefined,
                    };
                    messages.push(message);
                });
            }
            return messages;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return [];
        });
    return response;
};

export const historyGenerate = async (
    options: ConversationRequest,
    abortSignal: AbortSignal,
    contractIds: string | string[],
    convId?: string,
): Promise<Response> => {
    // Ensure contractIds is always an array
    const contractIdArray = Array.isArray(contractIds) ? contractIds : [contractIds];

    let body;
    if (convId) {
        body = JSON.stringify({
            conversation_id: convId,
            messages: options.messages,
            contract_workspace_ids: contractIdArray,
        });
    } else {
        body = JSON.stringify({
            messages: options.messages,
            contract_workspace_ids: contractIdArray,
        });
    }
    const response = await fetchWithAuth(host + "/chat/history/generate", {
        method: "POST",
        body: body,
        signal: abortSignal,
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return new Response();
        });
    return response;
};

export const historyUpdate = async (
    messages: ChatMessage[],
    convId: string,
): Promise<Response> => {
    const response = await fetchWithAuth(host + "/chat/history/update", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            messages: messages,
        }),
    })
        .then(async (res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyDelete = async (convId: string): Promise<Response> => {
    const response = await fetchWithAuth(host + "/chat/history/delete", {
        method: "DELETE",
        body: JSON.stringify({
            conversation_id: convId,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyDeleteAll = async (): Promise<Response> => {
    const response = await fetchWithAuth(host + "/chat/history/delete_all", {
        method: "DELETE",
        body: JSON.stringify({}),
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyClear = async (convId: string): Promise<Response> => {
    const response = await fetchWithAuth(host + "/chat/history/clear", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyRename = async (
    convId: string,
    title: string,
): Promise<Response> => {
    const response = await fetchWithAuth(host + "/chat/history/rename", {
        method: "POST",
        body: JSON.stringify({
            conversation_id: convId,
            title: title,
        }),
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export const historyEnsure = async (): Promise<CosmosDBHealth> => {
    const response = await fetchWithAuth(host + "/chat/history/ensure", {
        method: "GET",
    })
        .then(async (res) => {
            let respJson = await res.json();
            let formattedResponse;
            if (respJson.message) {
                formattedResponse = CosmosDBStatus.Working;
            } else {
                if (res.status === 500) {
                    formattedResponse = CosmosDBStatus.NotWorking;
                } else if (res.status === 401) {
                    formattedResponse = CosmosDBStatus.InvalidCredentials;
                } else if (res.status === 422) {
                    formattedResponse = respJson.error;
                } else {
                    formattedResponse = CosmosDBStatus.NotConfigured;
                }
            }
            if (!res.ok) {
                return {
                    cosmosDB: false,
                    status: formattedResponse,
                };
            } else {
                return {
                    cosmosDB: true,
                    status: formattedResponse,
                };
            }
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            console.log(err);
            return {
                cosmosDB: false,
                status: err,
            };
        });
    return response;
};

export const frontendSettings = async (): Promise<Response | null> => {
    const response = await fetchWithAuth(host + "/frontend_settings", {
        method: "GET",
    })
        .then((res) => {
            return res.json();
        })
        .catch((err) => {
            console.error("There was an issue fetching your data.");
            return null;
        });

    return response;
};

export const historyMessageFeedback = async (
    convId: string | undefined,
    messageId: string,
    feedback: string,
    additionalFeedback: string,
): Promise<Response> => {
    const response = await fetchWithAuth(
        host + "/chat/threads/" + convId + "/messages/" + messageId,
        {
            method: "POST",
            body: JSON.stringify({
                conversation_id: convId,
                message_id: messageId,
                message_feedback: feedback,
                additional_feedback: additionalFeedback,
            }),
        },
    )
        .then((res) => {
            return res;
        })
        .catch((err) => {
            console.error("There was an issue logging feedback.");
            let errRes: Response = {
                ...new Response(),
                ok: false,
                status: 500,
            };
            return errRes;
        });
    return response;
};

export async function downloadExport(
    site_index: string,
    fileId: number,
): Promise<string> {
    const response = await fetchWithAuth(
        host + "/" + "default" + "/download_export/" + fileId,
        {
            method: "GET",
        },
    );
    if (!response.ok) {
        throw new Error(
            `Failed to download file (file_id: ${fileId}): ${response.status}`,
        );
    }
    const responseJson = await response.json();
    return responseJson.sasUrl;
}

export async function listReadyFiles() {
    const response = await fetchWithAuth(host + "/default/ready_file", {
        method: "GET",
    });
    if (!response.ok) {
        throw new Error(`Failed to get files: ${response.status}`);
    }

    const responseJson = await response.json();

    const newFiles: any[] = [];
    Object.keys(responseJson).forEach((fileKey) => {
        const file = responseJson[fileKey];

        if (file["Metadata"]) {
            file["Metadata"] = JSON.parse(file["Metadata"]);
        }
        newFiles.push(file);
    });
    return responseJson;
}

export async function listFiles(): Promise<ListOfFiles[]> {
    const response = await fetchWithAuth(host + "/default/file", {
        method: "GET",
    });
    if (!response.ok) {
        toast.error(`Failed to get files: ${response.status}`);
        throw new Error(`Failed to get files: ${response.status}`);
    }

    const responseJson = await response.json();

    const newFiles: any[] = [];
    Object.keys(responseJson).forEach((fileKey) => {
        const file = responseJson[fileKey];

        if (file["Metadata"]) {
            file["Metadata"] = JSON.parse(file["Metadata"]);
        }
        newFiles.push(file);
    });
    return responseJson;
}

export async function uploadFile(
    formData: FormData,
    onProgress: (percent: number) => void,
) {
    const response = await fetchWithAuth(host + `/default/file`, {
        method: "POST",
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.status}`);
    }

    // Check if response body is available
    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("Failed to get response body for progress tracking.");
    }

    const contentLength = response.headers.get("Content-Length");
    const totalLength = contentLength ? parseInt(contentLength, 10) : null;

    let receivedLength = 0;
    const chunks = [];

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;

        // if (totalLength) {
        //     const percentComplete = (receivedLength / totalLength) * 100;
        //     onProgress(percentComplete);
        // }
    }

    return new Response(new Blob(chunks)); 
}

export async function getDocuments(id:any, search:any, page:any, rowsperpage:any) {
    const response = await fetchWithAuth(host + `/contract-mgmt/files?contract_workspace_id=${id}&name=${search}&page_number=${page}&page_size=${rowsperpage}`, {
        method: "GET",
    });
    const responseJson = await response.json();
    return responseJson;
}

export async function DeleteCWrkspace(id: string) {
    const response = await fetchWithAuth(host + `/contract-mgmt/contract?contract_workspace_id=${id}`, {
        method: "DELETE",
    });

    const responseJson = await response.json();
    return responseJson;
}

export async function DeleteFiles(fileId: any) {
    return fetchWithAuth(host + `/contract-mgmt/file/${fileId}`, {
        method: "DELETE",
        headers: {
            "Content-Type": "application/json",
        },
    }).then(async (res) => {
        return await res.json();
    });
}

export function updateFileType(fileId: number, fileType: string) {
    return fetchWithAuth(host + `/contract-mgmt/file/update-type`, {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({
            file_id: fileId,
            file_type: fileType
        })
    }).then(async (res) => {
        return await res.json();
    });
}

export async function updateComments(payload:any) {
    const response = await fetchWithAuth(host + "/contract-mgmt/contract", {
        method: "PUT",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    const responseJson = await response.json();
    return responseJson;
}

export async function getUpdateRateCard(cwID:number, formData:{}, questionVal:string, addEditRow:boolean): Promise<any> {
    const response = await fetchWithAuth(host + `/attribute-mgmt/answers?question_set=${questionVal}&contract_workspace_id=${cwID}&add_row=${addEditRow}`, {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
            "Content-Type": "application/json",
        },
    });

    const responseJson = await response.json();
    return responseJson;
}

export async function getDeleteUpdateRateCard(cwID:number, formData:{}, questionVal:string, deleteType:string): Promise<any> {
    const response = await fetchWithAuth(host + `/attribute-mgmt/answers?question_set=${questionVal}&contract_workspace_id=${cwID}&delete_type=${deleteType}`, {
        method: "Delete",
        body: JSON.stringify(formData),
        headers: {
            "Content-Type": "application/json",
        },
    });

    const responseJson = await response.json();
    return responseJson;
}

export const getPBIConfig = async (): Promise<any> => {
    const response = await fetchWithAuth(
        host + `/contract-comparison/getembedinfo`,
        {
            method: "GET",
        },
    );
    const payload = await response.json();
    return payload;
};

export async function exportDocuments(fileId:any) {
    const response:any = await fetchWithAuth(host + `/contract-mgmt/download-file?file_id=${fileId}`, {
        method: "GET",
    });

    const res = await response.json();
    if(res && res.success) {
        const fileUrl = res?.data?.url;
        
        let a = document.createElement('a');
        a.href = fileUrl;
        a.target = "_blank";
        a.download = `${fileId}.xlsx`; 
        
        document.body.appendChild(a);
        a.click(); 
        document.body.removeChild(a);
    }
    
    const responseJson = res;
    return responseJson;
}

export async function viewContractFile(fileId: number) {
    const response = await fetchWithAuth(host + "/default/contract/" + fileId, {
        method: "GET",
    });
    if (!response.ok) {
        throw new Error(`Failed to view contract: ${response.status}`);
    }

    const responseJson = await response.json();

    return responseJson;
}

export async function DeleteFile(fileId: number) {
    const response = await fetchWithAuth(host + "/default/file/" + fileId, {
        method: "DELETE",
    });
    if (!response.ok) {
        throw new Error(`Failed to Delete File: ${response.status}`);
    }

    const responseJson = await response.json();

    return responseJson;
}

export async function getContractStatus(): Promise<contractStatus[]> {
    try {
        const response = await fetchWithAuth(host + "/default/contract", {
            method: "GET",
        });

        if (!response.ok) {
            toast.error(`Failed to get status: ${response.status}`);
            throw new Error(`Failed to get status: ${response.status}`);
        }

        const responseJson = await response.json();
        
        return responseJson;
    } catch (error) {
        console.error("Error fetching contract status:", error);
        toast.error("An error occurred while fetching contract status.");
        throw error; 
    }
}

export const prelogin = async (
    UserData: UserData
): Promise<any> => {
    try {
        const response = await fetch(host + "/auth/pre-login", {
            method: "POST",
            body: JSON.stringify({
                email: UserData.email,
                password: UserData.password,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const getEmailAuthcode = async (
    UserData: UserData
): Promise<any> => {
    try {
        const response = await fetch(host + "/auth/mfa-code/mail", {
            method: "POST",
            body: JSON.stringify({
                email: UserData.email,
                password: UserData.password,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });  
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const setupmfa = async (
    UserMfaSetupInfo: UserMfaSetupInfo
): Promise<any> => {
    try {
        const response = await fetch(host + "/auth/mfa-setup", {
            method: "POST",
            body: JSON.stringify({
                email: UserMfaSetupInfo.email,
                password: UserMfaSetupInfo.password,
                code: UserMfaSetupInfo.code,
                mfa_secret: UserMfaSetupInfo.mfa_secret,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const loginLocal = async (
    UserLocalInfo: UserLocalInfo
): Promise<any> => {
    try {
        const response = await fetch(host + "/auth/login", {
            method: "POST",
            body: JSON.stringify({
                email: UserLocalInfo.email,
                password: UserLocalInfo.password,
                code: UserLocalInfo.code,
                code_source: UserLocalInfo.code_source,
            }),
            headers: {
                "Content-Type": "application/json",
            },
        });     
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const passwordEmailGenerator = async (userMail: string): Promise<any> => {
    try {
        const response = await fetch(`${host}/auth/forgot-password?email=${userMail}`, {
            method: "GET",
            headers: {
                "Content-Type": "application/json",
            },
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const getUserList = async (search:string, page:number, rowsPerPage:number): Promise<any> => {
    try {
        const response = await fetchWithAuth(
            host + `/user-mgmt/users?page_number=${page}&page_size=${rowsPerPage}&name=${search}`,
            {
                method: "GET",
            }
        );
        return await response.json();
    } catch (error) {
        console.error("Error fetching user list:", error);
        throw error;
    }
};

export const getUserListDropdowns = async (): Promise<any> => {
    try {
        const response = await fetchWithAuth(
            host + `/user-mgmt/user-creation-data`,
            {
                method: "GET",
            },
        );     
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const DeleteUser = async (val:any): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + "/user-mgmt/users", {
            method: "DELETE",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(val), // val = {user_ids : [23]}
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const CreateUser = async (form_data:any): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + "/user-mgmt/users", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(form_data),
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const reset_password = async (verify_reset_Data:any): Promise<any> => {
    try {
        const response = await fetch(host + "/auth/forgot-password", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${verify_reset_Data.token}`,
            },
            body: JSON.stringify(
                {new_password:verify_reset_Data.password}),
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const verifyPassword = async (verify_reset_Data:any): Promise<any> => {
    try {
        const response = await fetch(host + "/auth/verify", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${verify_reset_Data.token} `,
            },
            body: JSON.stringify(
                {new_password:verify_reset_Data.password}),
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const updateUser = async (form_data:any): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + "/user-mgmt/users", {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(form_data), 
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const updateUserIndex = async (userId: string | number, indexId: string | number): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + `/user-mgmt/users/${userId}/user-index`, {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ index_id: indexId }), 
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const grantPermission = async (userId: number, contractId: number): Promise<any> => {
    try {
        const response = await fetchWithAuth(
            host + `/user-mgmt/grant_permission`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    granted_user_id: userId,
                    contract_id: contractId,
                    permission_name: "CONTRIBUTOR"
                }),
            }
        );
        return await response.json();
    } catch (error) {
        console.error("Error granting permission:", error);
        throw error;
    }
};

export const revokePermission = async (userId: number, contractId: number): Promise<any> => {
    try {
        const response = await fetchWithAuth(
            host + `/user-mgmt/revoke_permission`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    granted_user_id: userId,
                    contract_id: contractId,
                    permission_name: "CONTRIBUTOR"
                }),
            }
        );
        return await response.json();
    } catch (error) {
        console.error("Error revoking permission:", error);
        throw error;
    }
};

export const getClients = async (search:string, page:number, rowsPerPage:number): Promise<any> => {
    try {
        const response = await fetchWithAuth(
            host + `/client-mgmt/clients?page_number=${page}&page_size=${rowsPerPage}&name=${search}`,
            {
                method: "GET",
            },
        );     
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const Createclient = async (form_data:any): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + "/client-mgmt/clients", {
            method: "POST",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(form_data),
        });
        
        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const updateClient = async (form_data:any): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + "/client-mgmt/clients", {
            method: "PUT",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(form_data), 
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export const DeleteClient = async (val:any): Promise<any> => {
    try {
        const response = await fetchWithAuth(host + "/client-mgmt/clients", {
            method: "DELETE",
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(val), 
        });

        if(response) {
            return response.json();
        }
    }
    catch(error:any) {
        return error.response;
    }
};

export async function getContractList(search:string, orderBy:string = '') {
    const response = await fetchWithAuth(host + `/contract-mgmt/contracts?name=${search}&order_by=${orderBy}`, {
        method: "GET",
    });
    const responseJson = await response.json();
    return responseJson;
}

export async function getContractsWithDetails(search:string = '') {
    const response = await fetchWithAuth(host + `/contract-mgmt/contracts-v2?with_details=true&search=${encodeURIComponent(search)}`, {
        method: "GET",
    });
    const responseJson = await response.json();
    return responseJson;
}

export async function getContractAttributes(cwID:number, questionVal:string, search:string = "", attributeFilter:string = ""): Promise<any> {
    let url = `${host}/attribute-mgmt/answers?question_set=${questionVal}&contract_workspace_id=${cwID}`;
    
    // Add attrb_name filter for price question set or when attributeFilter is provided
    if (questionVal === "price" && attributeFilter) {
        url += `&attrb_name=${encodeURIComponent(attributeFilter)}`;
    } else if (search) {
        url += `&attrb_name=${encodeURIComponent(search)}`;
    }
    
    const response = await fetchWithAuth(url, {
        method: "GET",
    });

    const responseJson = await response.json();
    return responseJson;
}

export async function getUpdateAttributes(cwID:number, formData:{}, questionVal:string): Promise<any> {
    const response = await fetchWithAuth(host + `/attribute-mgmt/answers?question_set=${questionVal}&contract_workspace_id=${cwID}`, {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
            "Content-Type": "application/json",
        },
    });
    const responseJson = await response.json();
    return responseJson;
}

export function retryContract(contractId: string) {
    return fetchWithAuth(`${host}/contract-mgmt/contracts/${contractId}/retry`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
    })
        .then((response) => response.json())
        .catch((error) => console.error(error));
}

export async function createCW(payload:any) {
    const response = await fetchWithAuth(host + "/contract-mgmt/contract", {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
    });
    const responseJson = await response.json();
    return responseJson;
}

export async function getExportData(cwID:number, questionVal:string): Promise<any> {
    const response:any = await fetchWithAuth(host + `/attribute-mgmt/export-answers?question_set=${questionVal}&contract_workspace_id=${cwID}`, {
        method: "GET",
    });

    const res = await response.json();
    if(res && res.success) {
        const fileUrl = res?.data?.url;
        
        let a = document.createElement('a');
        a.href = fileUrl;
        a.target = "_blank";
        a.download = `${questionVal}_${cwID}.xlsx`; 
        
        document.body.appendChild(a);
        a.click(); 
        document.body.removeChild(a);
    }
    
    return res;
}

export async function getBulkExportData(): Promise<any> {
    const response:any = await fetchWithAuth(host + `/attribute-mgmt/bulk-export`, {
        method: "GET",
    });

    const res = await response.json();
    if(res && res.success) {
        const fileUrl = res?.data?.url;
        
        let a = document.createElement('a');
        a.href = fileUrl;
        a.target = "_blank";
        a.download = `bulk_export.xlsx`; 
        
        document.body.appendChild(a);
        a.click(); 
        document.body.removeChild(a);
    }
    
    return res;
}

export async function getDeleteAttributes(cwID:number, formData:{}, questionVal:string): Promise<any> {
    const response = await fetchWithAuth(host + `/attribute-mgmt/answers?question_set=${questionVal}&contract_workspace_id=${cwID}`, {
        method: "Delete",
        body: JSON.stringify(formData),
        headers: {
            "Content-Type": "application/json",
        },
    });
    const responseJson = await response.json();
    return responseJson;
}

export async function uploadFiles(
    formData: FormData,
    cwId: string
) {
    const response = await fetchWithAuth(host + `/contract-mgmt/file?contract_workspace_id=${cwId}`, {
        method: "POST",
        body: formData,
    });

    if (!response.ok) {
        throw new Error(`Failed to upload file: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
        throw new Error("Failed to get response body for progress tracking.");
    }

    const contentLength = response.headers.get("Content-Length");
    const totalLength = contentLength ? parseInt(contentLength, 10) : null;

    let receivedLength = 0;
    const chunks = [];

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        chunks.push(value);
        receivedLength += value.length;
    }
    
    return new Response(new Blob(chunks));
}